import { createApi } from '@reduxjs/toolkit/query/react'
import baseQueryWithReauth from './baseQueryWithReauth';

const type = "role";

const usersInRole = "usersInRole";

// Define a service using a base URL and expected endpoints
export const roleApi = createApi({
  reducerPath: 'roleApi',
  baseQuery: baseQueryWithReauth,
  refetchOnMountOrArgChange: 0,
  keepUnusedDataFor: 0,
  tagTypes: [type, usersInRole],
  endpoints: (builder) => ({
    getRole: builder.query<any, { roleId: string }>({      
      query: (arg) => {
        const { roleId } = arg;
        return `role/${roleId}`
      },
      providesTags: (returnValue, args) => { return [{ type: type , id: 'LIST' }, ...(returnValue?.data || []).map((role: any) => ({ type: type, id: role.id }))]}
    }),  
    getUserPermissions: builder.query<any, void>({      
      query: () => {       
        return `role/userPermissions`
      }
    }),
    copyRole: builder.query<any, { roleId: string }>({      
      query: (arg) => {
        const { roleId } = arg;
        return `role/copy/${roleId}`
      },
    }),  
    getRoles: builder.query<any, void>({
      query: () => {              
        return `role/list` },
        providesTags: (returnValue, args) => { return [{ type: type , id: 'LIST' }, ...(returnValue?.roles || []).map((role: any) => ({ type: type, id: role.id }))]}
    }),  
    getRolesSummary: builder.query<any, void>({
      query: () => {              
        return `role/summaryList` },
        providesTags: (returnValue, args) => { return [{ type: type , id: 'LIST' }, ...(returnValue?.roles || []).map((role: any) => ({ type: type, id: role.id }))]}
    }),  
    getUserInRole: builder.query<any, { roleId: string }>({      
      query: (arg) => {
        const { roleId } = arg;
        return `role/usersInRole/${roleId}`
      },
      providesTags: (result) =>
        // is result available?
        {
        return result?.success
          ? // successful query
            [
              ...result.users.map(({ id }: any) => ({ type: usersInRole, id } as const)),
              { type: usersInRole, id: 'LIST' },
            ]
          : // an error occurred, but we still want to refetch this query when `{ type: 'Posts', id: 'LIST' }` is invalidated
            [{ type: usersInRole, id: 'LIST' }]
          },
    }),  
    removeRole: builder.mutation<any, {userId: string, roleId: string}>({
      query: (formData: any) => {   
        const { userId, roleId} = formData;
        return {
        url: `user/${userId}/removeRole/${roleId}`,
        method: 'PUT',
        body: formData
      }},
      invalidatesTags: [{ type: usersInRole, id: 'LIST' }],
    }),
    removeUsersFromRole: builder.mutation<any, { userIds: string[], roleId: string }>({
      query: (formData: any) => {   
        const { roleId} = formData;
        return {
        url: `role/${roleId}/removeUsers`,
        method: 'PUT',
        body: formData
      }},
      invalidatesTags: [{ type: usersInRole, id: 'LIST' }],
    }),
    getResources: builder.query<any, void>({
      query: () => {         
        return `role/resources` },
    }),      
    add: builder.mutation<any, any>({
      query: (formData: any) => {        
        return {
        url: `role`,
        method: 'POST',
        body: formData
      }},
      invalidatesTags: [type],
    }),  
    update: builder.mutation<any, any>({
      query: (formData: any) => {       
        const { roleId } = formData;
        return {
        url: `role/${roleId}`,
        method: 'PUT',
        body: formData
      }},
      invalidatesTags: [type],
    }),
    delete: builder.mutation<any, any>({
      query: (formData: any) => {
        const { roleId } = formData;
        return {
        url: `role/${roleId}`,
        method: 'DELETE'
      }},
      invalidatesTags: [{ type: type, id: 'LIST' }]
    }),
    deleteMultiple: builder.mutation<any, any>({
      query: (formData: any) => {
        return {
        url: `role`,
        method: 'DELETE',
        body: formData
      }},
      invalidatesTags: [{ type: type, id: 'LIST' }]
    })
  }),
})

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const { useGetRoleQuery, 
               useGetUserPermissionsQuery,
               useGetRolesQuery,
               useGetRolesSummaryQuery,
               useGetResourcesQuery,
               useAddMutation, 
               useUpdateMutation, 
               useDeleteMutation,
               useDeleteMultipleMutation,
               useCopyRoleQuery,
               useGetUserInRoleQuery,
               useRemoveRoleMutation,
               useRemoveUsersFromRoleMutation
             } = roleApi;